"use client";

import * as React from "react";
import { useCommandState } from "cmdk";
import { Clock3, CornerDownLeft, Search } from "lucide-react";

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useSidebar } from "@/components/ui/sidebar";
import {
  type AdminNavigationDestination,
  flattenAdminNavigation,
  type AdminNavigationGroup,
} from "@/lib/adminNavigation";
import { cn } from "@/lib/utils";
import { useRouter } from "@/lib/router-compat";

function NavigationSearchResultCount() {
  const resultCount = useCommandState((commandState) =>
    commandState.search ? commandState.filtered.count : 0
  );
  const search = useCommandState((commandState) => commandState.search);

  return (
    <span className="sr-only" aria-live="polite">
      {search ? `${resultCount}件のメニューが見つかりました。` : ""}
    </span>
  );
}

export function NavigationSearch({
  groups,
  appearance = "icon",
  recentDestinations = [],
  showShortcutHint = true,
}: {
  groups: readonly AdminNavigationGroup[];
  appearance?: "icon" | "field";
  recentDestinations?: readonly AdminNavigationDestination[];
  showShortcutHint?: boolean;
}) {
  const router = useRouter();
  const { setOpenMobile } = useSidebar();
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const returnFocusRef = React.useRef<HTMLElement | null>(null);
  const destinations = React.useMemo(
    () => flattenAdminNavigation(groups),
    [groups]
  );

  const restoreFocus = React.useCallback(() => {
    window.requestAnimationFrame(() => {
      const focusTarget = returnFocusRef.current;
      if (focusTarget?.isConnected) {
        focusTarget.focus();
      } else {
        triggerRef.current?.focus();
      }
      returnFocusRef.current = null;
    });
  }, []);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (!nextOpen) {
        setQuery("");
        restoreFocus();
      }
    },
    [restoreFocus]
  );

  const openSearch = React.useCallback(() => {
    returnFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : triggerRef.current;
    setOpen(true);
  }, []);

  React.useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if (
        event.isComposing ||
        event.repeat ||
        !(event.metaKey || event.ctrlKey) ||
        event.key.toLowerCase() !== "k"
      ) {
        return;
      }

      event.preventDefault();
      if (open) {
        handleOpenChange(false);
      } else {
        returnFocusRef.current =
          document.activeElement instanceof HTMLElement
            ? document.activeElement
            : triggerRef.current;
        setOpen(true);
      }
    };

    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, [handleOpenChange, open]);

  const handleSelect = (url: string) => {
    returnFocusRef.current = null;
    setOpen(false);
    setOpenMobile(false);
    router.push(url);
  };

  const showRecent = query.trim() === "" && recentDestinations.length > 0;

  const renderDestination = (destination: AdminNavigationDestination) => {
    const Icon = destination.icon;
    const hierarchy = [destination.groupLabel, destination.parentTitle]
      .filter(Boolean)
      .join(" › ");

    return (
      <CommandItem
        key={destination.id}
        value={`${destination.title} ${destination.parentTitle ?? ""}`}
        keywords={[...(destination.keywords ?? [])]}
        onSelect={() => handleSelect(destination.url)}
        className="min-h-12 cursor-pointer py-2.5"
      >
        {Icon ? <Icon className="size-4 shrink-0" aria-hidden="true" /> : null}
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium">
            {destination.title}
          </span>
          <span className="text-muted-foreground block truncate text-xs">
            {hierarchy}
          </span>
        </span>
        <CornerDownLeft
          className="text-muted-foreground size-3.5 shrink-0 opacity-60"
          aria-hidden="true"
        />
      </CommandItem>
    );
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label="メニューを検索"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-keyshortcuts="Meta+K Control+K"
        title="メニューを検索（⌘ / Ctrl + K）"
        onClick={openSearch}
        className={cn(
          "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground focus-visible:ring-sidebar-ring flex shrink-0 cursor-pointer touch-manipulation items-center rounded-lg font-normal transition-colors focus-visible:outline-none focus-visible:ring-2",
          appearance === "field"
            ? "border-sidebar-border bg-sidebar-accent/45 h-11 min-w-0 flex-1 justify-start gap-2 border px-3 text-sm group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:size-9 group-data-[collapsible=icon]:flex-none group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:border-0 group-data-[collapsible=icon]:bg-transparent group-data-[collapsible=icon]:px-0"
            : "size-11 justify-center md:size-7"
        )}
      >
        <Search className="size-4 shrink-0" aria-hidden="true" />
        {appearance === "field" ? (
          <>
            <span className="min-w-0 flex-1 truncate text-left group-data-[collapsible=icon]:hidden">
              機能を検索
            </span>
            {showShortcutHint ? (
              <kbd className="text-sidebar-foreground/60 hidden rounded border px-1.5 py-0.5 font-sans text-[10px] group-data-[collapsible=icon]:hidden sm:inline">
                ⌘K
              </kbd>
            ) : null}
          </>
        ) : (
          <span className="sr-only">メニューを検索</span>
        )}
      </button>

      <CommandDialog
        open={open}
        onOpenChange={handleOpenChange}
        title="メニューを検索"
        description="メニュー名や業務用語から管理画面の移動先を検索します。"
        className="top-[max(5rem,env(safe-area-inset-top))] max-h-[min(42rem,calc(100dvh-7rem))] translate-y-0 overflow-hidden sm:max-w-xl"
      >
        <CommandInput
          aria-label="メニュー名や業務用語を検索"
          value={query}
          onValueChange={setQuery}
          autoComplete="off"
          spellCheck={false}
          placeholder="メニュー名や業務用語で検索…"
        />
        <NavigationSearchResultCount />
        <CommandList
          label="メニュー検索結果"
          className="max-h-[min(32rem,calc(100dvh-13rem))] overscroll-contain"
        >
          <CommandEmpty>
            該当するメニューがありません。別の言葉で検索してください。
          </CommandEmpty>
          {showRecent ? (
            <CommandGroup heading="最近使った機能">
              {recentDestinations.map((destination) => (
                <React.Fragment key={destination.id}>
                  {renderDestination(destination)}
                </React.Fragment>
              ))}
              <div className="text-muted-foreground flex items-center gap-2 px-2 py-2 text-xs">
                <Clock3 className="size-3.5" aria-hidden="true" />
                機能名を入力すると、すべてのメニューから検索できます
              </div>
            </CommandGroup>
          ) : null}
          {!showRecent &&
            groups.map((group) => {
              const groupDestinations = destinations.filter(
                (destination) => destination.groupId === group.id
              );
              if (groupDestinations.length === 0) return null;

              return (
                <CommandGroup key={group.id} heading={group.label}>
                  {groupDestinations.map(renderDestination)}
                </CommandGroup>
              );
            })}
        </CommandList>
      </CommandDialog>
    </>
  );
}
